'use client'

import {
  type ComponentProps,
  type FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useCdnContext, useCdnMediaServer } from '../../cdn'
import { urlIsValid } from '../../url-handling'
import { IMAGE_PRODUCT_FALLBACK_SRC } from '../IMAGE_PRODUCT_FALLBACK_SRC'
import {
  imageAccessorGetImageSizes,
  imageAccessorGetImageSourceSizes,
} from '../imageAccessor'

type PictureProps = ComponentProps<'picture'> & {
  alt: string
  imageSizes: [number, number][]
  lazy?: boolean
  smallestImageSize: number
  src: string
}

export const Picture: FC<PictureProps> = ({
  alt,
  imageSizes,
  lazy = false,
  smallestImageSize,
  src,
  ...restProps
}) => {
  const { productImageCdnUrl } = useCdnContext()
  const [hasError, setHasError] = useState(!urlIsValid(src))
  const imgRef = useRef<HTMLImageElement>(null)
  const { setMediaServerOrigin } = useCdnMediaServer()

  const computedImageSizes = useMemo(
    () =>
      hasError
        ? undefined
        : imageAccessorGetImageSizes(imageSizes, smallestImageSize),
    [hasError, imageSizes, smallestImageSize],
  )

  const srcSetForWebp = useMemo(
    () =>
      hasError
        ? undefined
        : imageAccessorGetImageSourceSizes({
            cdnUrl: productImageCdnUrl,
            setExtensionToWebp: true,
            sizes: imageSizes,
            src,
          }),
    [hasError, imageSizes, productImageCdnUrl, src],
  )

  const srcSetForDefault = useMemo(
    () =>
      hasError
        ? undefined
        : imageAccessorGetImageSourceSizes({
            cdnUrl: productImageCdnUrl,
            sizes: imageSizes,
            src,
          }),
    [hasError, imageSizes, productImageCdnUrl, src],
  )

  const handleOnError = (): void => {
    setHasError(true)
  }

  /**
   * There are race conditions between image loading and hydration. If the browser
   * throws any errors before the hydration is run, those errors are lost, and we
   * need to programmatically determine that they happened.
   *
   * If the browser sets the "complete" flag to "true" but there isn't any image
   * loaded ("naturalHeight" is 0), we can assume that there was an error.
   *
   *
   * See https://github.com/facebook/react/issues/15446 for additional info
   */
  useEffect(() => {
    if (
      !hasError &&
      imgRef.current?.complete &&
      imgRef.current.naturalHeight === 0
    ) {
      setHasError(true)
    }
  }, [hasError])

  return (
    <picture {...restProps}>
      <source
        sizes={computedImageSizes}
        srcSet={srcSetForWebp}
        type="image/webp"
      />
      <img
        alt={alt}
        className="size-full object-contain"
        loading={lazy ? 'lazy' : 'eager'}
        onError={handleOnError}
        ref={imgRef}
        sizes={computedImageSizes}
        src={hasError ? setMediaServerOrigin(IMAGE_PRODUCT_FALLBACK_SRC) : src}
        srcSet={srcSetForDefault}
      />
    </picture>
  )
}
